import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Share2, Sparkles, ShoppingBag, TrendingDown, Users, Zap, Eye, Rocket, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtPrice } from "@/lib/currency";
import { CATEGORIES } from "@/constants/categories";
import { trackFeed, deviceId } from "@/lib/feed";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1615788189819-bee84874da4b?auto=format&fit=crop&w=1200&q=70";
const catLabel = (slug) => CATEGORIES.find((c) => c.slug === slug)?.label || slug;

export function FeedCard({ item, onSaved }) {
  const nav = useNavigate();
  const ref = useRef(null);
  const enteredAt = useRef(null);
  const [saved, setSaved] = useState(item.saved);
  const [count, setCount] = useState(item.save_count || 0);
  const [burst, setBurst] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.intersectionRatio >= 0.6) {
        enteredAt.current = Date.now();
        trackFeed({ product_id: item.id, type: "impression", category: item.category, seller_id: item.seller_id });
      } else if (enteredAt.current) {
        const secs = (Date.now() - enteredAt.current) / 1000;
        enteredAt.current = null;
        trackFeed({ product_id: item.id, type: secs < 1 ? "skip" : "dwell", seconds: secs, category: item.category, seller_id: item.seller_id });
      }
    }, { threshold: [0.6] });
    io.observe(el);
    return () => io.disconnect();
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSave = async () => {
    const next = !saved;
    setSaved(next);
    setCount((c) => Math.max(0, c + (next ? 1 : -1)));
    if (next) { setBurst(true); setTimeout(() => setBurst(false), 700); }
    trackFeed({ product_id: item.id, type: next ? "save" : "unsave", category: item.category, seller_id: item.seller_id });
    try {
      const { data } = await api.post(`/feed/save/${item.id}?device_id=${deviceId()}`);
      setSaved(data.saved); setCount(data.save_count); onSaved?.(item.id, data.saved);
    } catch { setSaved(!next); }
  };

  const share = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/product/${item.id}?to=feed`;
    const text = `${item.title} — ${fmtPrice(item.price, item.currency)} on VISMARKS`;
    trackFeed({ product_id: item.id, type: "share", category: item.category, seller_id: item.seller_id });
    if (navigator.share) { try { await navigator.share({ title: item.title, text, url }); } catch { /* cancelled */ } return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, "_blank", "noopener");
    try { await navigator.clipboard.writeText(url); toast.success("Feed link copied — opens right on this product"); } catch { /* clipboard blocked */ }
  };

  const open = (type) => {
    trackFeed({ product_id: item.id, type, category: item.category, seller_id: item.seller_id });
    nav(`/product/${item.id}`);
  };

  const b = item.badges || {};
  return (
    <article ref={ref} data-testid={`feed-card-${item.id}`} className="relative h-[var(--feed-h,calc(100dvh-64px))] md:h-[82vh] md:min-h-[600px] w-full snap-start snap-always overflow-hidden bg-[#121212] md:rounded-2xl md:border md:border-[#27272A]">
      <img src={item.image_url || FALLBACK_IMG} alt={item.title} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} onClick={() => open("tap")}
        className="absolute inset-0 h-full w-full object-cover cursor-pointer" data-testid="feed-card-image" />
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent pointer-events-none" />

      <div className="absolute top-16 md:top-4 left-4 flex flex-wrap gap-2 max-w-[70%]" data-testid="feed-card-badges">
        {b.boosted && <Badge cls="bg-[#FF3B30]/25 text-[#FF8A80] border-[#FF3B30]/40"><Rocket className="h-3 w-3" /> Boosted</Badge>}
        {b.new_today && <Badge cls="bg-emerald-500/20 text-emerald-300 border-emerald-400/30"><Sparkles className="h-3 w-3" /> New today</Badge>}
        {b.price_drop ? <Badge cls="bg-rose-500/20 text-rose-300 border-rose-400/30"><TrendingDown className="h-3 w-3" /> Price drop</Badge> : null}
        {b.viewing_now ? <Badge cls="bg-blue-500/20 text-blue-300 border-blue-400/30 animate-pulse"><Users className="h-3 w-3" /> {b.viewing_now} viewing now</Badge> : null}
        {b.only_left ? <Badge cls="bg-amber-500/20 text-amber-300 border-amber-400/30"><Zap className="h-3 w-3" /> Only {b.only_left} left</Badge> : null}
      </div>

      <div className="absolute right-3 bottom-28 md:bottom-24 z-10 flex flex-col items-center gap-4" data-testid="feed-action-rail">
        <Rail onClick={toggleSave} testId="feed-save-button" label={count > 0 ? count : "Save"} active={saved}>
          <motion.span animate={burst ? { scale: [1, 1.45, 1] } : { scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 15 }} className="relative inline-flex">
            <Heart className={`h-6 w-6 ${saved ? "fill-[#FF3B30] text-[#FF3B30]" : ""}`} />
            <AnimatePresence>{burst && <motion.span initial={{ scale: 0.4, opacity: 0.9 }} animate={{ scale: 2.4, opacity: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0 rounded-full bg-[#FF3B30]/50 blur-sm" />}</AnimatePresence>
          </motion.span>
        </Rail>
        <Rail onClick={() => { trackFeed({ product_id: item.id, type: "want_similar", category: item.category }); nav(`/open-demands?want=${encodeURIComponent(item.title)}`); }} testId="feed-want-similar-button" label="Want"><Sparkles className="h-6 w-6" /></Rail>
        <Rail onClick={share} testId="feed-share-button" label="Share"><Share2 className="h-6 w-6" /></Rail>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-5 pr-20 md:pr-24">
        <div className="flex items-center gap-2 text-xs text-zinc-300">
          <Link to={`/store/${item.seller_id}`} className="font-semibold hover:underline" data-testid="feed-card-seller">{item.seller_name || "VISMARKS seller"}</Link>
          <span className="text-zinc-500">&middot;</span>
          <span className="text-zinc-400">{catLabel(item.category)}</span>
          {item.founding_seller && <span className="ml-1 px-1.5 py-0.5 rounded bg-[#FF3B30]/20 text-[#FF8A80] text-[10px] font-bold uppercase tracking-wider">Founding seller</span>}
        </div>
        <h2 onClick={() => open("tap")} className="mt-1.5 font-black text-xl sm:text-2xl tracking-tight leading-tight text-zinc-50 line-clamp-2 cursor-pointer" data-testid="feed-card-title">{item.title}</h2>
        {item.description && <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{item.description}</p>}
        <div className="mt-3 flex items-end justify-between gap-3">
          <div>
            <div className="font-mono font-black text-2xl text-[#34C759] tabular-nums" data-testid="feed-card-price">{fmtPrice(item.price, item.currency)}</div>
            {b.price_drop ? <div className="font-mono text-xs text-zinc-500 line-through">{fmtPrice(b.price_drop, item.currency)}</div> : <div className="text-[11px] text-zinc-500 flex items-center gap-1"><Eye className="h-3 w-3" /> {item.views} views</div>}
          </div>
          {item.can_buy === false ? (
            <button onClick={() => open("enquire")} data-testid="feed-enquire-button" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#FF9F0A] hover:bg-[#E68F09] active:scale-95 transition-[background-color,transform] text-black font-bold text-sm">
              <MessageSquare className="h-4 w-4" /> Enquire
            </button>
          ) : (
            <button onClick={() => open("buy")} data-testid="feed-buy-button" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#FF3B30] hover:bg-[#E03126] active:scale-95 transition-[background-color,transform] text-white font-bold text-sm">
              <ShoppingBag className="h-4 w-4" /> Buy now
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function Badge({ children, cls }) {
  return <span className={`inline-flex items-center gap-1 text-[11px] font-bold tracking-wider uppercase px-2 py-1 rounded-full border backdrop-blur-md ${cls}`}>{children}</span>;
}

function Rail({ children, label, onClick, testId, active }) {
  return (
    <button onClick={onClick} data-testid={testId} className="flex flex-col items-center gap-1 text-white">
      <span className={`h-12 w-12 rounded-full flex items-center justify-center backdrop-blur-md border transition-colors ${active ? "bg-[#FF3B30]/20 border-[#FF3B30]/50" : "bg-black/40 border-white/15 hover:bg-black/60"}`}>{children}</span>
      <span className="text-[11px] font-semibold text-zinc-200 drop-shadow">{label}</span>
    </button>
  );
}
